/**
 * Paleta de comandos (Ctrl/Cmd + K).
 *
 * Es el atajo que evita la navegacion por menus anidados: se escribe parte del
 * nombre de la pantalla y se entra. Las entradas salen del mismo arbol de menus
 * que la barra lateral, asi que un modulo nuevo aparece aca sin registrar nada.
 *
 * La busqueda ignora acentos y admite subsecuencias ("liqex" encuentra
 * "Liquidación de expensas").
 */

import clsx from 'clsx';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { create } from 'zustand';

import { IconBuilding, IconLayers, IconLogout, IconMonitor, IconSearch } from '@/components/shell/icons';
import { fuzzyScore, menuPath, navigableMenus } from '@/components/shell/menu-utils';
import { Kbd } from '@/components/shell/primitives';
import { useMenusQuery } from '@/components/shell/queries';
import { leerRecientes, registrarUso } from '@/components/shell/recent';
import { useTheme } from '@/components/shell/theme';
import { notify } from '@/components/shell/toast';
import { esAdministrador } from '@/panel/acceso';
import { selectCompanies, useSessionStore } from '@/store/session';

// ---------------------------------------------------------------------------
// Estado de apertura (compartido con la barra superior)
// ---------------------------------------------------------------------------
interface PaletteState {
  open: boolean;
  setOpen: (open: boolean) => void;
  toggle: () => void;
}

export const usePaletteStore = create<PaletteState>()((set) => ({
  open: false,
  setOpen: (open) => {
    set({ open });
  },
  toggle: () => {
    set((estado) => ({ open: !estado.open }));
  },
}));

type PaletteGroup = 'Buscar' | 'Recientes' | 'Ir a' | 'Empresa' | 'Aplicación';

interface PaletteItem {
  id: string;
  label: string;
  /** Contexto a la derecha: la rama del menu o el modelo. */
  hint?: string;
  group: PaletteGroup;
  /** Texto extra que participa de la busqueda sin mostrarse. */
  keywords?: string;
  icon: 'nav' | 'company' | 'app' | 'logout' | 'search';
  run: () => void;
}

const ICONOS = {
  nav: IconLayers,
  company: IconBuilding,
  app: IconMonitor,
  logout: IconLogout,
  search: IconSearch,
} as const;

export function CommandPalette() {
  const abierto = usePaletteStore((estado) => estado.open);
  const setOpen = usePaletteStore((estado) => estado.setOpen);
  const toggle = usePaletteStore((estado) => estado.toggle);

  const navigate = useNavigate();
  const { data: menus } = useMenusQuery();
  const companies = useSessionStore(selectCompanies);
  const setActiveCompany = useSessionStore((estado) => estado.setActiveCompany);
  const signOut = useSessionStore((estado) => estado.signOut);
  const tenant = useSessionStore((estado) => estado.tenant);
  const user = useSessionStore((estado) => estado.user);
  const { cycle, resolved } = useTheme();

  // Misma regla que en la barra lateral: el atajo al panel se ofrece sólo a
  // quien puede entrar. Una paleta que lista destinos que devuelven «no tenés
  // acceso» deja de ser un atajo.
  const puedeAdministrar = esAdministrador(user);

  // Historial de uso, por organización: el experto vuelve a las mismas pantallas.
  const scope = tenant === null ? 'anon' : String(tenant.id);
  // Se relee cada vez que se abre la paleta (por eso `abierto` en las deps): así
  // el orden refleja lo último que se usó, incluso lo de hace un instante.
  const recientes = useMemo(() => leerRecientes(scope), [scope, abierto]);

  const [consulta, setConsulta] = useState('');
  const [indice, setIndice] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);
  const listaRef = useRef<HTMLUListElement>(null);
  const focoPrevio = useRef<HTMLElement | null>(null);

  // Atajo global. Se registra una vez y convive con el resto de la app.
  useEffect(() => {
    const alTeclear = (evento: KeyboardEvent) => {
      if ((evento.metaKey || evento.ctrlKey) && evento.key.toLowerCase() === 'k') {
        evento.preventDefault();
        toggle();
      }
    };
    window.addEventListener('keydown', alTeclear);
    return () => {
      window.removeEventListener('keydown', alTeclear);
    };
  }, [toggle]);

  // Al abrir: se recuerda quien tenia el foco para devolverselo al cerrar, y se
  // bloquea el scroll del fondo para que el gesto de scroll no mueva la pagina.
  useEffect(() => {
    if (!abierto) return;
    focoPrevio.current = document.activeElement as HTMLElement | null;
    setConsulta('');
    setIndice(0);
    const overflowPrevio = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const temporizador = window.setTimeout(() => inputRef.current?.focus(), 0);
    return () => {
      window.clearTimeout(temporizador);
      document.body.style.overflow = overflowPrevio;
      focoPrevio.current?.focus();
    };
  }, [abierto]);

  const items = useMemo<PaletteItem[]>(() => {
    const salida: PaletteItem[] = [];

    for (const entrada of navigableMenus(menus ?? [])) {
      const modelo = entrada.node.model;
      const destino = menuPath(entrada.node);
      if (modelo === null || destino === null) continue;
      const rama = entrada.path.slice(0, -1).join(' › ');
      const item: PaletteItem = {
        id: `menu:${entrada.node.name}`,
        label: entrada.node.label,
        group: 'Ir a',
        // Los sinonimos del modulo entran igual que la ruta y el modelo: quien
        // aprieta Ctrl+K y escribe «expensas» tiene que llegar a «Liquidacion».
        keywords: `${entrada.path.join(' ')} ${modelo} ${entrada.synonyms}`,
        icon: 'nav',
        run: () => {
          // Se navega al MENU, no al modelo: entrar por «Clientes» tiene que
          // llegar filtrado, igual que desde la barra lateral.
          navigate(destino);
        },
      };
      if (rama !== '') item.hint = rama;
      salida.push(item);
    }

    if (companies.length > 1) {
      for (const company of companies) {
        salida.push({
          id: `company:${company.id}`,
          label: company.name,
          hint: 'Cambiar de empresa',
          group: 'Empresa',
          keywords: 'empresa consorcio company',
          icon: 'company',
          run: () => {
            void setActiveCompany(company.id).then((listo) => {
              if (listo) notify.success('Empresa activa', company.name);
              else notify.error('No se pudo cambiar de empresa');
            });
          },
        });
      }
    }

    salida.push(
      {
        id: 'app:home',
        label: 'Ir al inicio',
        group: 'Aplicación',
        keywords: 'inicio dashboard tablero',
        icon: 'app',
        run: () => {
          navigate('/');
        },
      },
      {
        id: 'app:theme',
        label: `Cambiar tema (ahora: ${resolved === 'dark' ? 'oscuro' : 'claro'})`,
        group: 'Aplicación',
        keywords: 'tema oscuro claro modo noche contraste',
        icon: 'app',
        run: cycle,
      },
      {
        id: 'app:logout',
        label: 'Cerrar sesión',
        group: 'Aplicación',
        keywords: 'salir logout desconectar',
        icon: 'logout',
        run: signOut,
      },
    );

    if (puedeAdministrar) {
      salida.push({
        id: 'app:panel',
        label: 'Panel de control',
        hint: 'Administración del sistema',
        group: 'Aplicación',
        keywords: 'panel consola administracion permisos roles usuarios modulos estado del sistema',
        icon: 'app',
        run: () => {
          navigate('/panel');
        },
      });
    }

    return salida;
  }, [menus, companies, navigate, setActiveCompany, signOut, cycle, resolved, puedeAdministrar]);

  // Ranking de reciente: 0 = lo último que se usó. Sirve para dos cosas —armar
  // el grupo «Recientes» con la caja vacía y desempatar a favor de lo conocido
  // cuando se escribe—.
  const rankReciente = useMemo(() => {
    const mapa = new Map<string, number>();
    recientes.forEach((id, i) => mapa.set(id, i));
    return mapa;
  }, [recientes]);

  const resultados = useMemo(() => {
    if (consulta.trim() === '') {
      // Caja vacía: primero lo reciente (como grupo propio), después el resto en
      // el orden natural. Un item reciente no se repite abajo.
      const porId = new Map(items.map((item) => [item.id, item]));
      const recientesItems: PaletteItem[] = [];
      for (const id of recientes) {
        const original = porId.get(id);
        if (original !== undefined) recientesItems.push({ ...original, group: 'Recientes' });
      }
      const usados = new Set(recientes);
      const resto = items.filter((item) => !usados.has(item.id));
      return [...recientesItems, ...resto];
    }
    // ESTA PALETA BUSCA PANTALLAS, NO REGISTROS, Y ESO NO CAMBIA.
    //
    // Meterle registros la volvería otra cosa: una consulta al servidor por
    // tecla, resultados de tres modelos mezclados con las pantallas y sin lugar
    // para el camino («de qué administración es este consorcio»), que es la
    // mitad de lo que se pidió. Lo que sí hace falta es que quien escribe
    // «Kloster» acá —y hoy recibe «Sin resultados», porque ninguna PANTALLA se
    // llama así— tenga a un Enter de distancia la búsqueda que sí lo encuentra.
    //
    // Va primero y en su propio grupo, con el texto tal cual se escribió.
    const escrito = consulta.trim();
    const puntuados: Array<{ item: PaletteItem; score: number }> = [];
    for (const item of items) {
      const directo = fuzzyScore(item.label, consulta);
      const contexto = fuzzyScore(`${item.label} ${item.hint ?? ''} ${item.keywords ?? ''}`, consulta);
      let mejor = Math.max(directo ?? -Infinity, (contexto ?? -Infinity) - 50);
      // Empujón por uso reciente: modesto, para reordenar empates sin tapar una
      // coincidencia textual claramente mejor. Lo más reciente pesa un poco más.
      const rank = rankReciente.get(item.id);
      if (mejor > -Infinity && rank !== undefined) mejor += Math.max(0, 40 - rank * 5);
      if (mejor > -Infinity) puntuados.push({ item, score: mejor });
    }
    puntuados.sort((a, b) => b.score - a.score);
    const pantallas = puntuados.map((entrada) => entrada.item);

    if (escrito.length < 2) return pantallas;
    const buscar: PaletteItem = {
      id: 'app:buscar',
      label: `Buscar «${escrito}» en la cartera`,
      hint: 'Administraciones, administradores y consorcios',
      group: 'Buscar',
      icon: 'search',
      run: () => {
        navigate(`/buscar?q=${encodeURIComponent(escrito)}`);
      },
    };
    return [buscar, ...pantallas];
  }, [items, consulta, recientes, rankReciente, navigate]);

  useEffect(() => {
    setIndice(0);
  }, [consulta]);

  // Mantiene visible la opcion resaltada al moverse con el teclado.
  useEffect(() => {
    if (!abierto) return;
    const activa = listaRef.current?.querySelector('[data-activa="true"]');
    activa?.scrollIntoView({ block: 'nearest' });
  }, [indice, abierto]);

  if (!abierto) return null;

  const ejecutar = (item: PaletteItem | undefined) => {
    if (item === undefined) return;
    // Se anota lo que lleva a algún lado o cambia de empresa. Alternar tema o
    // cerrar sesión no son destinos: encabezar «Recientes» con ellos sería ruido.
    if (item.id !== 'app:theme' && item.id !== 'app:logout') registrarUso(scope, item.id);
    setOpen(false);
    item.run();
  };

  let grupoAnterior: PaletteGroup | null = null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-start justify-center p-3 sm:p-6"
      role="dialog"
      aria-modal="true"
      aria-label="Paleta de comandos"
    >
      <div
        className="absolute inset-0 bg-fg/25 backdrop-blur-[2px] animate-fade-in"
        onClick={() => {
          setOpen(false);
        }}
        aria-hidden="true"
      />

      <div className="relative mt-[8vh] w-full max-w-xl overflow-hidden rounded-xl border border-line bg-surface shadow-lg animate-slide-up">
        <div className="flex items-center gap-2.5 border-b border-line px-4">
          <IconSearch className="h-[1.125rem] w-[1.125rem] shrink-0 text-fg-faint" />
          <input
            ref={inputRef}
            type="text"
            value={consulta}
            onChange={(evento) => {
              setConsulta(evento.target.value);
            }}
            onKeyDown={(evento) => {
              if (evento.key === 'Escape') {
                evento.preventDefault();
                setOpen(false);
              } else if (evento.key === 'ArrowDown') {
                evento.preventDefault();
                setIndice((actual) => (resultados.length === 0 ? 0 : (actual + 1) % resultados.length));
              } else if (evento.key === 'ArrowUp') {
                evento.preventDefault();
                setIndice((actual) =>
                  resultados.length === 0 ? 0 : (actual - 1 + resultados.length) % resultados.length,
                );
              } else if (evento.key === 'Home') {
                evento.preventDefault();
                setIndice(0);
              } else if (evento.key === 'End') {
                evento.preventDefault();
                setIndice(Math.max(0, resultados.length - 1));
              } else if (evento.key === 'Enter') {
                evento.preventDefault();
                ejecutar(resultados[indice]);
              } else if (evento.key === 'Tab') {
                // Unico control enfocable del dialogo: se evita que Tab saque el
                // foco al contenido de atras, que sigue siendo inerte.
                evento.preventDefault();
              }
            }}
            role="combobox"
            aria-expanded="true"
            aria-controls="paleta-resultados"
            aria-activedescendant={resultados.length > 0 ? `paleta-opcion-${indice}` : undefined}
            aria-autocomplete="list"
            autoComplete="off"
            spellCheck={false}
            placeholder="Pantallas, empresas y acciones — o un nombre para buscar en la cartera…"
            className="h-14 w-full bg-transparent text-base outline-none placeholder:text-fg-faint"
          />
          <Kbd>Esc</Kbd>
        </div>

        <ul
          ref={listaRef}
          id="paleta-resultados"
          role="listbox"
          aria-label="Resultados"
          className="max-h-[min(60vh,26rem)] overflow-y-auto p-1.5"
        >
          {resultados.length === 0 && (
            // `presentation` en los <li>: entre un listbox y sus options no puede
            // haber nodos con rol propio, y la lista sigue siendo semantica en HTML.
            <li role="presentation" className="px-3 py-8 text-center text-sm text-fg-muted">
              Sin resultados para <span className="font-medium text-fg">{consulta}</span>
            </li>
          )}

          {resultados.map((item, posicion) => {
            const Icono = ICONOS[item.icon];
            const nuevoGrupo = item.group !== grupoAnterior;
            grupoAnterior = item.group;
            const activa = posicion === indice;

            return (
              <li key={item.id} role="presentation">
                {nuevoGrupo && (
                  <p
                    role="presentation"
                    className="px-2.5 pb-1 pt-3 text-2xs font-semibold uppercase tracking-wide text-fg-faint first:pt-1"
                  >
                    {item.group}
                  </p>
                )}
                <div
                  id={`paleta-opcion-${posicion}`}
                  role="option"
                  aria-selected={activa}
                  data-activa={activa}
                  onClick={() => {
                    ejecutar(item);
                  }}
                  onPointerEnter={() => {
                    setIndice(posicion);
                  }}
                  className={clsx(
                    'flex cursor-pointer items-center gap-3 rounded-md px-2.5 py-2.5 text-sm',
                    activa ? 'bg-accent-soft text-accent' : 'text-fg',
                  )}
                >
                  <Icono className={clsx('h-4 w-4 shrink-0', activa ? 'text-accent' : 'text-fg-faint')} />
                  <span className="truncate font-medium">{item.label}</span>
                  {item.hint !== undefined && (
                    <span className="ml-auto hidden truncate pl-3 text-xs text-fg-faint sm:block">
                      {item.hint}
                    </span>
                  )}
                </div>
              </li>
            );
          })}
        </ul>

        <div className="hidden items-center gap-4 border-t border-line bg-app-subtle px-4 py-2 text-xs text-fg-faint sm:flex">
          <span className="flex items-center gap-1.5">
            <Kbd>↑</Kbd>
            <Kbd>↓</Kbd> navegar
          </span>
          <span className="flex items-center gap-1.5">
            <Kbd>↵</Kbd> abrir
          </span>
          <span className="ml-auto flex items-center gap-1.5">
            <Kbd>Ctrl</Kbd>
            <Kbd>K</Kbd> abrir esta paleta
          </span>
        </div>
      </div>
    </div>
  );
}
